<div class="video-call-container">
  @if (isRecording()) {
    <div class="recording-indicator">
      <mat-icon>screen_record</mat-icon>
      <span>{{ 'VideoCall.Recording' | translate }}</span>
    </div>
  }

  <!-- STATE 1: Pre-call / Joining Screen -->
  @if (!isMeetingActive()) {
    <div class="join-call-wrapper">
      @if (isDrawer()) {
        <div class="lobby-header">
          <h2>{{ 'Global.VideoCall' | translate }}</h2>
          <button mat-icon-button class="lobby-close-btn" (click)="closeDrawer()" [disabled]="loading()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }
      <div class="lobby-scroll-container px-3 py-4 custom-scrollbar">
        <app-call-invitation [callID]="callID()" [parentLoading]="loading()" />
        <app-join-call-settings
          [username]="username()"
          (usernameChange)="username.set($event)"
          (joinCall)="onJoinCall()"
          [loading]="loading()"
          [isDrawer]="true"
          [callID]="callID()"
          [isHost]="isHost()"
          [waitingParticipants]="waitingParticipants()"
          (admitParticipant)="onAdmitParticipant($event)"
          (rejectParticipant)="onRejectParticipant($event)"
        />
        @if (error()) {
          <div class="error-message">{{ error() }}</div>
        }
      </div>
    </div>
  }

  <!-- STATE 2: In-call Screen -->
  @if (isMeetingActive()) {
    <!-- Main content area that switches between video and chat -->
    <div class="main-content">
      <div class="media-container">
        <div class="video-grid">
          <!-- Local User's Video -->
          @if (localParticipant(); as p) {
            <div class="video-cell" [class.is-speaking]="p.attendee.chimeAttendeeId === activeSpeakerId()">
              @if (p.tileState; as tile) {
                <app-video-tile [tileState]="tile" [isLocal]="true" />
              } @else {
                <div class="no-video-placeholder">
                  <mat-icon>person</mat-icon>
                </div>
              }
              <div class="participant-name">{{ 'You' | translate }}</div>
            </div>
          }

          <!-- The Screen Share itself -->
          @if (screenShareTile(); as tile) {
            <div class="screen-share-view">
              <app-video-tile [tileState]="tile" [canBeEnlarged]="true" [enableZoom]="true" />
            </div>
          }

          <!-- Remote Participants' Videos -->
          @for (p of remoteParticipants(); track p.attendee.chimeAttendeeId) {
            <div class="video-cell" [class.is-speaking]="p.attendee.chimeAttendeeId === activeSpeakerId()">
              @if (p.tileState; as tile) {
                <app-video-tile [tileState]="tile" [isLocal]="false" [canBeEnlarged]="true" />
              } @else {
                <div class="no-video-placeholder">
                  <mat-icon>person</mat-icon>
                  @if (isHost() && p.attendee.isPstn) {
                    <button mat-icon-button class="pstn-hangup-btn" (click)="onHangupPstnAttendee($event, p.attendee.chimeAttendeeId)">
                      <mat-icon>call_end</mat-icon>
                    </button>
                  }
                </div>
              }
              <div class="participant-name">{{ p.attendee.username }}</div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Call Controls are always visible at the bottom -->
    <app-call-controls
      [isDrawer]="isDrawer()"
      [micOn]="!chimeService.isMuted()"
      [cameraActive]="chimeService.isCameraEnabled()"
      [chatActive]="chatOpen()"
      [isRecording]="isRecording()"
      [isHost]="isHost()"
      [participantCount]="chimeService.roster().length"
      [waitingCount]="waitingParticipants().length"
      [isVideoFxActive]="isVideoFxActive()"
      [isScreenSharing]="isScreenSharing()"
      [callID]="callID()"
      (toggleMic)="chimeService.toggleMicrophone()"
      (toggleCamera)="chimeService.toggleCamera()"
      (toggleChat)="chatOpen.set(!chatOpen())"
      (leaveCall)="onLeaveCall()"
      (toggleRecording)="onToggleRecording()"
      (toggleScreenShare)="onToggleScreenShare($event)"
      (toggleParticipants)="participantsOpen.set(!participantsOpen())"
      (toggleVideoFx)="onToggleVideoFx()"
      (dialing)="onStartPstnCall($event)"
    />

    <!-- Participant List Panel -->
    @if (participantsOpen()) {
      <div class="participant-panel">
        <app-participant-list
          [roster]="chimeService.allParticipants()"
          [isHost]="isHost()"
          [localUserId]="localUserId()"
          [isAudioLocked]="chimeService.isAudioLocked()"
          [isCameraLocked]="chimeService.isCameraLocked()"
          [isScreenShareLocked]="chimeService.isScreenShareLocked()"
          [isChatLocked]="chimeService.isChatLocked()"
          [activeSpeakerId]="chimeService.activeSpeakerId()"
          [hostAttendeeIds]="chimeService.hostAttendeeIds()"
          [waitingParticipants]="waitingParticipants()"
          (muteParticipant)="chimeService.muteParticipant($event)"
          (stopVideoParticipant)="chimeService.stopVideoParticipant($event)"
          (kickParticipant)="chimeService.kickParticipant($event)"
          (promoteParticipant)="chimeService.promoteParticipant($event)"
          (muteAll)="chimeService.muteAllParticipants()"
          (toggleLockAudio)="chimeService.toggleLockAudio()"
          (toggleLockCamera)="chimeService.toggleLockCamera()"
          (toggleLockScreenShare)="chimeService.toggleLockScreenShare()"
          (toggleLockChat)="chimeService.toggleLockChat()"
          (closePanel)="participantsOpen.set(false)"
          (admitParticipant)="onAdmitParticipant($event)"
          (rejectParticipant)="onRejectParticipant($event)"
        />
      </div>
    }

    <!-- Chat Panel -->
    @if (chatOpen()) {
      <div class="chat-panel">
        <app-chat-box (closeChat)="chatOpen.set(false)" />
      </div>
    }
  }

  @if (isMeetingActive() && isHost() && !chatOpen()) {
    <div class="meeting-duration-overlay">
      <div class="meeting-duration-box">
        <mat-icon>schedule</mat-icon>
        <span>{{ chimeService.meetingDuration() }}</span>
      </div>
    </div>
  }

  @if (callStatusMessage()) {
    <div class="call-status-overlay">
      <div class="call-status-box">
        @if (callStatusMessage() !== 'Call answered.' && callStatusMessage() !== 'Call failed to start.' && !callStatusMessage().startsWith('Call failed:')) {
          <mat-spinner diameter="18"></mat-spinner>
        }
        <span>{{ callStatusMessage() }}</span>
      </div>
    </div>
  }
</div>
